import { normalizeText } from "../../../utils/normalizeText";
// import { Icon } from '@iconify-icon/react/dist/iconify.mjs';
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./style.css";
import AppIcon from "../../App/AppIcon";

interface CustomSelectProps {
  value: string | undefined;
  onChange: (val: string | undefined) => void;
  options: { label: string; value: string }[];
  showClear?: boolean;
  showSearch?: boolean;
  className?: string;
  error?: string;
  disabled?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ value, onChange, options, showClear, showSearch, className = "", error, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const selectRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLUListElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleSelect = (val: string) => {
    onChange(val);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleClear = () => {
    onChange(undefined);
    setIsOpen(false);
    setSearchQuery("");
  };

  const filteredOptions = options.filter((opt) => normalizeText(opt.label).includes(normalizeText(searchQuery)));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (selectRef.current && !selectRef.current.contains(target) && portalRef.current && !portalRef.current.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && selectRef.current) {
      const updatePosition = () => {
        const rect = selectRef.current?.getBoundingClientRect();
        if (rect) {
          setDropdownPosition({
            top: rect.bottom,
            left: rect.left,
            width: rect.width
          });
        }
      };
      
      // Update position immediately and after a short delay
      updatePosition();
      const timeoutId = setTimeout(updatePosition, 10);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  return (
    <div className={`custom-select-container ${className}`} ref={selectRef}>
      <div onClick={() => (disabled ? {} : setIsOpen(!isOpen))} className={`custom-select-trigger ${disabled ? "disabled" : ""}`}>
        <span className={value ? "" : "custom-select-placeholder"}>{selectedOption?.label || "Selecione..."}</span>
        <div className="custom-select-actions">
          {showClear && value && !disabled && (
            <button
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="custom-select-clear"
            >
              <AppIcon icon={"mdi:clear"} />X
            </button>
          )}
          <span>
            <AppIcon icon={"raphael:arrowdown"} width={12} />
          </span>
        </div>
      </div>

      {isOpen &&
        createPortal(
          <ul
            ref={portalRef}
            className={`custom-select-dropdown ${error ? "error" : ""}`}
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width || "auto",
            }}
          >
            {showSearch && (
              <li className="custom-select-search">
                <input type="text" placeholder="Buscar..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              </li>
            )}

            {filteredOptions.map((opt) => (
              <li key={opt.value} onClick={() => handleSelect(opt.value)} className={`custom-select-option ${opt.value === value ? "selected" : ""}`}>
                {opt.label}
              </li>
            ))}

            {filteredOptions.length === 0 && <li className="custom-select-no-options">Nenhuma opção encontrada</li>}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default CustomSelect;
